import * as React from 'react'
import { generateUUID } from '@/modules/shared/utils/id'
import { requestRemoveTimesheets } from '@/lib/api/company/timesheet/removeTimesheets/requestRemoveTimesheets'
import {
  waitForImportTask,
  TaskProgress,
} from '@/lib/api/config/importTask/status/waitForImportTask'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

// Group delete as a background task with the same per-driver progress the
// calculation flow shows — previously the request blocked for minutes (or,
// before that, fired one HTTP call per driver).
export function useRemoveTimesheets() {
  const [isPending, setIsPending] = React.useState(false)
  const [progress, setProgress] = React.useState<TaskProgress | null>(null)
  const { tSafe } = useLocalize()
  const mountedRef = React.useRef(true)
  React.useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  const removeTimesheets = React.useCallback(
    async ({
      companyId,
      driverIds,
      months,
      year,
    }: {
      companyId: string
      driverIds: number[]
      months: number[]
      year: number
    }) => {
      setIsPending(true)
      setProgress(null)
      try {
        const taskId = generateUUID()
        await requestRemoveTimesheets({
          id: taskId,
          companyId,
          driverIds,
          months,
          year,
        })
        await waitForImportTask({
          taskId,
          onProgress: setProgress,
          isCancelled: () => !mountedRef.current,
        })
      } finally {
        setIsPending(false)
        setProgress(null)
      }
    },
    [],
  )

  const baseStatusText = tSafe('timesheet.status-removing')
  const statusText = progress
    ? `${baseStatusText} ${progress.current}/${progress.total}`
    : baseStatusText

  return { removeTimesheets, isPending, statusText }
}
